import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { Component, inject, signal, input, effect, computed, untracked } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { ClubProfilePage, ResultService } from '../../services/result.service';
import { Result } from '../../models/data.models';
import { RouterModule } from '@angular/router';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { TranslationService } from '../../services/translation.service';
import { getResultDisciplineDefinition, normalizeResultDiscipline } from '../../models/discipline.models';
import { groupClubActivity, memberCategory, MemberAgeGroup } from './club-activity';
import { currentSeasonStartYear, seasonLabel } from '../../utils/season';
import { medalRank, countPodiums } from '../../models/podium.models';
import { normalizeBreedName } from '../../models/dog-breed.models';

interface ClubProfileStats {
  name: string;
  activeMemberCount: number;
  totalDistance: number;
  distanceBreakdown: {
    vtt: number;
    trott: number;
    running: number;
    runningChild: number;
    walk: number;
  };
  totalRaces: number;
  totalEvents: number;
  raceBreakdown: {
    vtt: number;
    trott: number;
    running: number;
    runningChild: number;
    walk: number;
  };
  podiums: {
    gold: { total: number; breakdown: { vtt: number; trott: number; running: number; runningChild: number; walk: number } };
    silver: { total: number; breakdown: { vtt: number; trott: number; running: number; runningChild: number; walk: number } };
    bronze: { total: number; breakdown: { vtt: number; trott: number; running: number; runningChild: number; walk: number } };
    chocolate: { total: number; breakdown: { vtt: number; trott: number; running: number; runningChild: number; walk: number } };
    total: number;
  };
  topSpeed: number;
  clubResults: Result[];
  activeMembersBreakdown: { ageGroup: MemberAgeGroup; total: number; homme: number; femme: number }[];
}

@Component({
  selector: 'app-club-profile',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, TranslatePipe, DistancePipe, RouterModule, BrandedLoaderComponent],
  templateUrl: './club-profile.component.html'
})
export class ClubProfileComponent {
  private resultService = inject(ResultService);
  private location = inject(Location);
  private translationService = inject(TranslationService);

  // Route parameter (withComponentInputBinding). A signal, so that moving from one club to another
  // on the same route reloads the page.
  clubName = input('');

  isLoading = signal(true);
  clubStats = signal<ClubProfileStats | null>(null);
  statsScope = signal<'season' | 'all'>('season');
  selectedSeasonStart = signal(currentSeasonStartYear());
  private clubSeasons = signal<number[]>([]);
  // Only the displayed scope (one season, or all of them) is fetched; each one is kept once loaded.
  private pageCache = new Map<string, ClubProfilePage>();
  private latestLoad = 0;
  /** Set once the visitor picks a period; until then the latest season with results is shown. */
  private periodChosen = false;
  availableSeasons = computed(() => [...new Set([currentSeasonStartYear(), this.selectedSeasonStart(), ...this.clubSeasons()])]
    .sort((a, b) => b - a));
  currentSeasonLabel = computed(() => seasonLabel(this.selectedSeasonStart()));
  statsScopeLabel = computed(() => this.statsScope() === 'season'
    ? `${this.translationService.translate('clubProfile.season')} ${this.currentSeasonLabel()}`
    : this.translationService.translate('clubProfile.allTime'));

  // One activity per event, using the same period as the statistics.
  recentActivity = computed(() => {
    const stats = this.clubStats();
    if (!stats || !stats.clubResults) return [];
    
    // Sort results by date descending if possible. 
    // Result object has 'race.event.date' or 'race.event?.date'
    return groupClubActivity(stats.clubResults);
  });

  dogBreedStats = computed(() => {
    const dogs = new Set<string>();
    const breeds = new Map<string, { name: string; count: number }>();
    for (const result of this.clubStats()?.clubResults || []) {
      const dog = result.team?.dog;
      const dogId = dog?.id || result.team?.dogId;
      const name = dog?.breed ? normalizeBreedName(dog.breed) : undefined;
      if (!dogId || !name) continue;
      const key = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (['unknown', 'inconnu', 'inconnue', 'non renseigne', 'non renseignee', 'n/a', '-', ''].includes(key) || dogs.has(dogId)) continue;
      dogs.add(dogId);
      const breed = breeds.get(key) || { name, count: 0 };
      breed.count++;
      breeds.set(key, breed);
    }
    return [...breeds.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });
  leadingDogBreeds = computed(() => this.dogBreedStats().filter(breed => breed.count === this.dogBreedStats()[0]?.count));

  constructor() {
    effect(() => {
      const name = this.clubName();
      untracked(() => {
        // Another club: start again from its latest season.
        this.clubStats.set(null);
        this.clubSeasons.set([]);
        this.statsScope.set('season');
        this.selectedSeasonStart.set(currentSeasonStartYear());
        this.periodChosen = false;
        if (name) void this.loadClubData(name);
      });
    });
  }

  goBack() {
    this.location.back();
  }

  getDisciplineLabel(discipline?: string): string {
    if (discipline === 'other') return this.translationService.translate('clubProfile.otherDiscipline');
    if (!discipline) return this.translationService.translate('common.course');
    const normalized = normalizeResultDiscipline(discipline);
    if (!normalized) return discipline;
    return this.translationService.translate(getResultDisciplineDefinition(normalized).labelKey);
  }

  formatTime(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds <= 0) return '—';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  async loadClubData(name: string): Promise<void> {
    const requestId = ++this.latestLoad;
    try {
      // Read untracked: this runs inside an effect that must not re-run when these signals change.
      const { season, firstLoad } = untracked(() => ({
        season: this.statsScope() === 'all' ? null : this.selectedSeasonStart(),
        firstLoad: !this.clubStats(),
      }));
      // The full-page loader also hides the season picker: only show it before the first content.
      if (firstLoad) this.isLoading.set(true);

      const decodedName = decodeURIComponent(name);
      const targetName = decodedName === '_INDEPENDENT_' ? 'Independents' : decodedName; // Handle alias if used

      const cacheKey = `${targetName}|${season ?? 'all'}`;
      let page = this.pageCache.get(cacheKey);
      if (!page) {
        page = await this.resultService.getClubProfilePage(targetName, season);
        this.pageCache.set(cacheKey, page);
      }
      // A newer scope was requested while this one loaded: let it win.
      if (requestId !== this.latestLoad) return;
      this.clubSeasons.set(page.seasons);

      // A new season starts without results: show the club's latest season instead.
      const latestSeason = page.seasons[0];
      if (season !== null && !this.periodChosen && page.results.length === 0
        && latestSeason !== undefined && latestSeason < season) {
        this.selectedSeasonStart.set(latestSeason);
        return this.loadClubData(name);
      }

      const statsResults = page.results;

      // Active Members (unique humans in results)
      const activeHumanIds = new Set<string>();
      statsResults.forEach(r => {
        const humanId = r.team?.human?.id || r.team?.humanId;
        if (humanId) activeHumanIds.add(humanId);
      });

      // Stats Calculation
      let totalDistance = 0;
      const distanceBreakdown = { vtt: 0, trott: 0, running: 0, runningChild: 0, walk: 0 };
      const uniqueEventIds = new Set<string>();
      const raceBreakdown = { vtt: 0, trott: 0, running: 0, runningChild: 0, walk: 0 };
      let topSpeed = 0;
      
      // Initialize detailed podiums structure
      const createBreakdown = () => ({ vtt: 0, trott: 0, running: 0, runningChild: 0, walk: 0 });
      const podiums = {
        gold: { total: 0, breakdown: createBreakdown() },
        silver: { total: 0, breakdown: createBreakdown() },
        bronze: { total: 0, breakdown: createBreakdown() },
        chocolate: { total: 0, breakdown: createBreakdown() },
        total: 0
      };

      statsResults.forEach(r => {
        if (r.event?.id) uniqueEventIds.add(r.event.id);

        const dist = r.race?.distanceKm || 0;
        totalDistance += dist;
        
        // Breakdown logic
        const disc = (normalizeResultDiscipline(r.discipline || r.race?.discipline, r.race?.name, r.category) || '').toLowerCase();
        
        let isVtt = false, isTrott = false, isWalk = false, isChild = false, isRun = false;

        if (disc.includes('vtt') || disc.includes('bike')) {
           distanceBreakdown.vtt += dist;
           isVtt = true;
        } else if (disc.includes('trott')) {
           distanceBreakdown.trott += dist;
           isTrott = true;
        } else if (disc.includes('marche')) {
           distanceBreakdown.walk += dist;
           isWalk = true;
        } else if (disc.includes('cadet') || disc.includes('minime') || disc.includes('benjamin') || disc.includes('enfant') || disc.includes('baby')) {
           distanceBreakdown.runningChild += dist;
           isChild = true;
        } else if (disc.includes('cross')) { // Catch-all for other Canicross (Long, Court, Standard)
           distanceBreakdown.running += dist;
           isRun = true;
        } else {
           // Fallback default to running if unsure/standard
           distanceBreakdown.running += dist;
           isRun = true;
        }

        // Speeds missing from the source are derived from distance and time (results are cached:
        // they are not modified).
        const speed = r.averageSpeedKmph
          || (r.timeSeconds > 0 && dist > 0 ? Number((dist / (r.timeSeconds / 3600)).toFixed(2)) : 0);
        if (speed > topSpeed) topSpeed = speed;

        const rank = medalRank(r);
        
        // Helper to increment breakdown
        const incrementBreakdown = (breakdown: { vtt: number; trott: number; running: number; runningChild: number; walk: number }) => {
             if (isVtt) breakdown.vtt++;
             else if (isTrott) breakdown.trott++;
             else if (isWalk) breakdown.walk++;
             else if (isChild) breakdown.runningChild++;
             else if (isRun) breakdown.running++;
        };

        if (rank === 1) {
            podiums.gold.total++;
            incrementBreakdown(podiums.gold.breakdown);
        } else if (rank === 2) {
            podiums.silver.total++;
            incrementBreakdown(podiums.silver.breakdown);
        } else if (rank === 3) {
            podiums.bronze.total++;
            incrementBreakdown(podiums.bronze.breakdown);
        } else if (rank === 4) {
            podiums.chocolate.total++;
            incrementBreakdown(podiums.chocolate.breakdown);
        }

      });
      podiums.total = countPodiums(statsResults).total;

      for (const event of groupClubActivity(statsResults)) {
        const sports = new Set<keyof typeof raceBreakdown>();
        for (const { discipline } of event.disciplines) {
          if (discipline === 'Cani-VTT') sports.add('vtt');
          else if (discipline === 'Cani-Trottinette') sports.add('trott');
          else if (discipline === 'Cani-Marche') sports.add('walk');
          else if (/Cadet|Minime|Benjamin/.test(discipline)) sports.add('runningChild');
          else if (/Canicross/.test(discipline)) sports.add('running');
        }
        for (const sport of sports) raceBreakdown[sport]++;
      }
      
      // Each active member counts once, in the category and gender of their first result.
      const groupedCategories = new Map<MemberAgeGroup, { total: number; homme: number; femme: number }>();
      const processedHumans = new Set<string>();
      statsResults.forEach(r => {
        const hId = r.team?.human?.id;
        if (!hId || processedHumans.has(hId)) return;
        processedHumans.add(hId);
        const { ageGroup, gender } = memberCategory(r.category, r.team?.human?.gender);
        const group = groupedCategories.get(ageGroup) ?? { total: 0, homme: 0, femme: 0 };
        group.total++;
        if (gender) group[gender]++;
        groupedCategories.set(ageGroup, group);
      });

      const activeMembersBreakdown = [...groupedCategories]
        .map(([ageGroup, counts]) => ({ ageGroup, ...counts }))
        .sort((a, b) => b.total - a.total);

      this.clubStats.set({
        name: targetName === 'Independents' ? this.translationService.translate('clubsKiosk.noClub') : targetName,
        activeMemberCount: activeHumanIds.size,
        totalDistance,
        distanceBreakdown: {
          ...distanceBreakdown,
        },
        totalRaces: uniqueEventIds.size,
        totalEvents: uniqueEventIds.size,
        raceBreakdown,
        podiums,
        topSpeed,
        clubResults: statsResults,
        activeMembersBreakdown
      });

    } catch (e) {
      console.error('Error loading club profile', e);
    } finally {
      if (requestId === this.latestLoad) this.isLoading.set(false);
    }
  }

  setStatsScope(scope: 'season' | 'all'): void {
    this.periodChosen = true;
    this.statsScope.set(scope);
    if (this.clubName()) void this.loadClubData(this.clubName());
  }

  setSeason(value: string): void {
    this.selectedSeasonStart.set(Number(value));
    this.setStatsScope('season');
  }


}
